import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  viewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormControl,
} from '@angular/forms';
import {
  authenticateMfaCode,
  dismissMfaError,
  navigateToOriginLogin,
} from '../../core/state/mfa/mfa.actions';
import { select, Store } from '@ngrx/store';
import { RootState } from '../../core/state/shared/root-state.interface';
import { first, Observable, takeWhile, tap, timer } from 'rxjs';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, DatePipe, KeyValuePipe } from '@angular/common';
import {
  selectMfaAnonymizedEmail,
  selectMfaHasFailed,
  selectMfaIsLoading,
} from '../../core/state/mfa/mfa.selectors';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MfaFailComponent } from '../../components/mfa-fail/mfa-fail.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { map } from 'rxjs/operators';
import { MfaService } from '../../core/http/mfa.service';
import { MfaErrors } from '../../core/state/mfa/mfa.state';

@Component({
  selector: 'app-two-factor',
  templateUrl: './two-factor.component.html',
  styleUrls: [
    'two-factor.component.scss',
    '../login-form/login-form.component.scss',
  ],
  imports: [
    MatCard,
    MatCardContent,
    ReactiveFormsModule,
    TranslateModule,
    MatFormField,
    MatInput,
    MatError,
    AsyncPipe,
    KeyValuePipe,
    MatCheckbox,
    MatIcon,
    MatIconButton,
    MfaFailComponent,
    MatProgressSpinner,
    DatePipe,
  ],
})
export class TwoFactorComponent implements OnInit, OnDestroy {
  otpInputs = viewChildren('otpInput', {
    read: ElementRef,
  });
  otpForm: FormGroup;
  alertMessage$: Observable<boolean>;
  isTimedOut = false;
  isLoading$: Observable<boolean>;
  anonymizedEmail$: Observable<string>;
  millisecondsRemaining$: Observable<number>;
  currentFocusedIndex = 0;

  private store = inject(Store<RootState>);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private timeoutId: ReturnType<typeof setTimeout>;
  private mfaService = inject(MfaService);

  ngOnInit() {
    this.otpForm = this.fb.group(
      Array.from({ length: 6 }, (_, i) => `otp${i}`).reduce(
        (formGroup, controlKey) => {
          formGroup[controlKey] = new FormControl('', [
            Validators.pattern('[0-9]{1}'),
          ]);
          return formGroup;
        },
        {
          rememberUser: new FormControl(false),
        },
      ),
    );

    this.anonymizedEmail$ = this.store
      .pipe(select(selectMfaAnonymizedEmail))
      .pipe(
        tap((email) => {
          if (email) {
            this.setTimer();
          }
        }),
      );

    this.alertMessage$ = this.store.pipe(select(selectMfaHasFailed)).pipe(
      tap((alert) => {
        this.setErrors(alert);
      }),
    );
    this.isLoading$ = this.store.pipe(select(selectMfaIsLoading));

    this.cdr.detectChanges();
    this.onFocus(0);

    this.setTimeout();
  }

  ngOnDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.otpForm.reset();
  }

  get otpControls() {
    return this.otpForm.controls;
  }

  onDismiss(): void {
    this.store.dispatch(navigateToOriginLogin({}));
  }

  onSubmit(): void {
    const otpControls = Object.entries(this.otpForm.controls).filter(([key]) =>
      key.startsWith('otp'),
    );

    if (
      this.otpForm.valid &&
      otpControls.every(([, control]) => control.value)
    ) {
      const otpValues = otpControls
        .map(([, control]) => control.value)
        .join('');

      this.store.dispatch(
        authenticateMfaCode({
          code: otpValues,
          rememberUser: this.otpForm.value.rememberUser,
        }),
      );
    }
  }

  onInput(index: number, event: any) {
    this.store.dispatch(dismissMfaError());
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    if (input.value) {
      if (index < this.otpInputs().length - 1) {
        this.onFocus(index + 1);
      }
      this.onSubmit();
    }
  }

  onKeyDown(index: number, event: any) {
    if (index > 0) {
      if (event.key === 'Backspace' && !event.target.value) {
        this.onFocus(index - 1, true);
      }
      if (event.key === 'ArrowLeft') {
        this.onFocus(index - 1);
      }
    }
    if (index < this.otpInputs().length - 1 && event.key === 'ArrowRight') {
      this.onFocus(index + 1);
    }
  }

  onPaste(event: ClipboardEvent) {
    // Get the pasted text and determine which input field was the target
    const pastedText = event.clipboardData?.getData('text') || '';
    const targetElement = event.target as HTMLInputElement;
    const startInputIndex = parseInt(targetElement.id.replace(/\D/g, ''), 10);

    // Process the pasted text if not empty
    if (pastedText) {
      // Prevent the default paste behavior
      event.preventDefault();

      // Track how many fields we'll fill
      let filledInputs = 0;
      const maxInputFields = 6;

      // Fill the OTP fields with valid digits from the pasted content
      for (
        let i = 0;
        i < pastedText.length &&
        startInputIndex + filledInputs < maxInputFields;
        i++
      ) {
        const char = pastedText[i];
        const currentInputIndex = startInputIndex + filledInputs;
        const currentInput = this.otpForm.controls[`otp${currentInputIndex}`];

        // Mark the field as touched for validation
        currentInput.markAsTouched();

        // Only accept numeric characters
        if (/^\d$/.test(char)) {
          currentInput.setValue(char);
          filledInputs++;
        } else {
          // Stop processing if we encounter a non-digit
          break;
        }
      }

      // If we haven't filled all the inputs, move the cursor to the next empty field
      const nextInputIndex = startInputIndex + filledInputs;
      if (nextInputIndex < maxInputFields) {
        this.onFocus(nextInputIndex);
      }

      // Try to submit the form after processing the paste
      this.onSubmit();
    }
  }

  onFocus(index: number, isBackspace = false) {
    this.currentFocusedIndex = index;
    setTimeout(() => {
      const inputElement = this.otpInputs()[index].nativeElement;
      inputElement.select();
      inputElement.focus();
      if (isBackspace) {
        inputElement.value = '';
      }
    }, 0);
  }

  onCheckboxClick() {
    // Maintain focus on current OTP input after checkbox click
    setTimeout(() => {
      this.onFocus(this.currentFocusedIndex);
    }, 0);
  }

  onResendEmail() {
    this.mfaService
      .resendEmailCode()
      .pipe(first())
      .subscribe(() => {
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
        }
        this.isTimedOut = false;
        this.setTimeout();
        this.setTimer();
      });
  }

  setErrors(hasFailed: boolean) {
    Object.keys(this.otpForm.controls).forEach((key) => {
      const control = this.otpForm.get(key);
      if (hasFailed) {
        control?.setErrors({ incorrect: true });
        control?.markAsTouched();
      } else {
        control?.setErrors(null);
        control?.markAsUntouched();
      }
    });
  }

  setTimeout(): void {
    this.timeoutId = setTimeout(() => {
      this.isTimedOut = true;
    }, 1800000); // 30 minutes
  }

  setTimer(): void {
    const endTime = Date.now() + 300000; // 5 minutes from now

    this.millisecondsRemaining$ = timer(0, 100).pipe(
      map(() => {
        const remaining = endTime - Date.now();
        return Math.max(0, remaining);
      }),
      takeWhile((remaining) => remaining > 0, true),
    );
  }

  protected readonly MfaErrors = MfaErrors;
}
